import { IconBigIcon48Gmail, IconBigIcon48Notion, IconResourcePreview30Notion, IconPath16Notion, IconSidebar24Notion, IconBigIcon48Github, IconResourcePreview30Github, IconPath16Github, IconSidebar24Github, IconBigIcon48Onedrive, IconBigIcon48Dropbox, IconResourcePreview30Dropbox, IconPath16Dropbox, IconSidebar24Dropbox, IconBigIcon48GoogleDrive, IconResourcePreview30GoogleDrive, IconPath16GoogleDrive, IconSidebar24GoogleDrive, IconBigIcon48Android, IconResourcePreview30Android, IconPath16Android, IconSidebar24Android, IconBigIcon48Apple, IconResourcePreview30Apple, IconPath16Apple, IconSidebar24Apple, IconBigIcon48Laptop, IconResourcePreview30Computer, IconPath16Computer, IconSidebar24Laptop, IconBigIcon48Phone, IconResourcePreview30Phone, IconPath16Phone, IconSidebar24Phone } from '@fabric-app/icons/svg';
import React__default from 'react';
import { appRoutes } from '../app/routing/routes.config.js';
import { generatePath } from '@fabric-app/utils/url/routing';
import { connectionInfoIconFactory, connectionInfoImageFactory } from './connections.utils.js';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { IconNotion, IconGithub, IconGoogleDrive, IconAndroid, IconApple, IconZapier, Img3dHammer, IconFigma, IconReadwise, IconChrome, IconLinear, IconDropbox, IconOnedrive } from '@fabric-app/icons/old';
import { SubscriptionTier } from '@fabric/woody-client';
import { captureException } from '@sentry/core';

const connectionsSupportedByFe = [
  "IOS_DEVICE",
  "ANDROID_DEVICE",
  "SCREENSHOT_SYNC",
  "GOOGLE_DRIVE",
  "DROPBOX",
  "ONEDRIVE",
  "GITHUB",
  "NOTION",
  "LOCAL_FS",
  "GMAIL"
];
const getToPathFirstMirror = (integration) => {
  if (!integration.mirrorRegistries[0]) {
    captureException(new Error("No mirror registry found for integration"), {
      extra: {
        integrationId: integration.id
      }
    });
    return "";
  }
  return generatePath(appRoutes.mirror.detail, {
    folderId: integration.mirrorRegistries[0].rootId
  });
};
const connectionsConfig = {
  SCREENSHOT_SYNC: {
    Icon: IconBigIcon48Phone,
    IconSidebar: IconSidebar24Phone,
    IconPath: IconPath16Phone,
    IconCardPreview: IconResourcePreview30Phone,
    IconCardStore: IconBigIcon48Phone,
    integrationName: "Mobile device sync",
    successMessage: "",
    successDescription: "",
    getTooltip: () => "Mobile device sync",
    getToPath: () => "",
    isSingleMirror: true,
    isDeviceSync: true,
    category: "device-sync",
    installation: {
      minimumTier: SubscriptionTier.PRO,
      description: "Sync your iPhone or Android with your Fabric.",
      infoList: {
        syncDirection: "one-way",
        viewInFabric: true,
        availability: "search-and-assist"
      },
      multipleInstallationsAllowed: true
    }
  },
  LOCAL_FS: {
    Icon: IconBigIcon48Laptop,
    IconSidebar: IconSidebar24Laptop,
    IconPath: IconPath16Computer,
    IconCardPreview: IconResourcePreview30Computer,
    IconCardStore: IconBigIcon48Laptop,
    integrationName: "Desktop file sync",
    successMessage: "Successfully installed.",
    successDescription: "Your desktop sync is now running.",
    getTooltip: () => "Desktop file sync",
    getToPath: getToPathFirstMirror,
    isSingleMirror: true,
    isDeviceSync: true,
    category: "device-sync",
    installation: {
      minimumTier: SubscriptionTier.PRO,
      description: "Sync a Fabric folder on your computer.",
      infoList: {
        syncDirection: "two-way",
        viewInFabric: true,
        availability: "search-and-assist"
      },
      availableSoon: true,
      multipleInstallationsAllowed: true
    }
  },
  IOS_DEVICE: {
    Icon: IconApple,
    IconSidebar: IconSidebar24Apple,
    IconPath: IconPath16Apple,
    IconCardPreview: IconResourcePreview30Apple,
    IconCardStore: IconBigIcon48Apple,
    integrationName: "iOS Screenshots",
    successMessage: "Your iOS screenshots are now connected.",
    successDescription: "Fabric will now start syncing your iOS screenshots.",
    getTooltip: () => "iOS Screenshots",
    getToPath: getToPathFirstMirror,
    isSingleMirror: true,
    isDeviceSync: true,
    category: "device-sync",
    installation: {
      minimumTier: SubscriptionTier.PRO,
      description: "Connect your iOS screenshots to Fabric.",
      infoList: {
        syncDirection: "one-way",
        viewInFabric: true,
        availability: "search-and-assist"
      },
      multipleInstallationsAllowed: true,
      hideFromInstallationList: true
    }
  },
  ANDROID_DEVICE: {
    Icon: IconAndroid,
    IconSidebar: IconSidebar24Android,
    IconPath: IconPath16Android,
    IconCardPreview: IconResourcePreview30Android,
    IconCardStore: IconBigIcon48Android,
    IconProps: {
      style: {
        color: "#3ddb85"
      }
    },
    integrationName: "Android Screenshots",
    successMessage: "Your Android screenshots are now connected.",
    successDescription: "Fabric will now start syncing your Android screenshots.",
    getTooltip: () => "Android Screenshots",
    getToPath: getToPathFirstMirror,
    isSingleMirror: true,
    isDeviceSync: true,
    category: "device-sync",
    installation: {
      minimumTier: SubscriptionTier.PRO,
      description: "Sync your Android screenshots with your Fabric, including every time you take a new one.",
      infoList: {
        syncDirection: "one-way",
        viewInFabric: true,
        availability: "search-and-assist"
      },
      multipleInstallationsAllowed: true,
      hideFromInstallationList: true
    }
  },
  GOOGLE_DRIVE: {
    Icon: IconGoogleDrive,
    IconSidebar: IconSidebar24GoogleDrive,
    IconPath: IconPath16GoogleDrive,
    IconCardPreview: IconResourcePreview30GoogleDrive,
    IconCardStore: IconBigIcon48GoogleDrive,
    integrationName: "Google Drive",
    successMessage: "Your Google Drive is now connected.",
    successDescription: "Fabric will now start syncing your Google Drive files.",
    getTooltip: (root) => `Google Drive - ${root.name}`,
    getToPath: (root) => generatePath(appRoutes.integrations.detail.traverseView, {
      integrationId: root.id,
      type: "google-drive"
    }),
    isSingleMirror: false,
    isDeviceSync: false,
    category: "cloud",
    installation: {
      minimumTier: SubscriptionTier.PRO,
      description: "Connect your Google Drive.",
      infoList: {
        syncDirection: "one-way",
        viewInFabric: true,
        availability: "search-and-assist"
      },
      multipleInstallationsAllowed: true
    }
  },
  DROPBOX: {
    Icon: IconBigIcon48Dropbox,
    IconSidebar: IconSidebar24Dropbox,
    IconPath: IconPath16Dropbox,
    IconCardPreview: IconResourcePreview30Dropbox,
    IconCardStore: IconBigIcon48Dropbox,
    integrationName: "Dropbox",
    successMessage: "Your Dropbox is now connected.",
    successDescription: "Fabric will now start syncing your Dropbox files.",
    getTooltip: (root) => `Dropbox - ${root.name}`,
    getToPath: (root) => generatePath(appRoutes.integrations.detail.traverseView, {
      integrationId: root.id,
      type: "dropbox"
    }),
    isSingleMirror: false,
    isDeviceSync: false,
    category: "cloud",
    installation: {
      minimumTier: SubscriptionTier.PRO,
      description: "Connect your Dropbox.",
      infoList: {
        syncDirection: "one-way",
        viewInFabric: true,
        availability: "search-and-assist"
      },
      multipleInstallationsAllowed: true
    }
  },
  ONEDRIVE: {
    Icon: IconBigIcon48Onedrive,
    IconSidebar: React__default.Fragment,
    IconPath: React__default.Fragment,
    IconCardPreview: React__default.Fragment,
    IconCardStore: IconBigIcon48Onedrive,
    integrationName: "OneDrive",
    successMessage: "Your OneDrive is now connected.",
    successDescription: "Fabric will now start syncing your OneDrive files.",
    getTooltip: (root) => `OneDrive - ${root.name}`,
    getToPath: (root) => generatePath(appRoutes.integrations.detail.traverseView, {
      integrationId: root.id,
      type: "one-drive"
    }),
    isSingleMirror: false,
    isDeviceSync: false,
    category: "cloud",
    installation: {
      minimumTier: SubscriptionTier.PRO,
      description: "Connect your OneDrive.",
      infoList: {
        syncDirection: "one-way",
        viewInFabric: true,
        availability: "search-and-assist"
      },
      multipleInstallationsAllowed: true,
      availableSoon: true
    }
  },
  GITHUB: {
    Icon: IconGithub,
    IconSidebar: IconSidebar24Github,
    IconPath: IconPath16Github,
    IconCardPreview: IconResourcePreview30Github,
    IconCardStore: IconBigIcon48Github,
    integrationName: "GitHub stars",
    successMessage: "Your GitHub account is now connected.",
    successDescription: "Fabric will now start syncing your starred repositories.",
    getTooltip: (root) => `GitHub  - ${root.name}`,
    getToPath: getToPathFirstMirror,
    isSingleMirror: true,
    isDeviceSync: false,
    category: "other",
    installation: {
      minimumTier: SubscriptionTier.PRO,
      description: "Connect your GitHub and every time you star a repo, it will be synced and indexed by Fabric.",
      infoList: {
        syncDirection: "one-way",
        viewInFabric: true,
        availability: "search-and-assist"
      },
      multipleInstallationsAllowed: false
    }
  },
  NOTION: {
    Icon: IconNotion,
    IconSidebar: IconSidebar24Notion,
    IconPath: IconPath16Notion,
    IconCardPreview: IconResourcePreview30Notion,
    IconCardStore: IconBigIcon48Notion,
    integrationName: "Notion",
    successMessage: "Your Notion workspace is now connected.",
    successDescription: "Fabric will now start syncing workspace pages.",
    getTooltip: (root) => `Notion  - ${root.name}`,
    getToPath: getToPathFirstMirror,
    isSingleMirror: true,
    isDeviceSync: false,
    category: "other",
    installation: {
      minimumTier: SubscriptionTier.PRO,
      description: "Connect your Notion account, and explore all your documents inside Fabric.",
      infoList: {
        syncDirection: "one-way",
        viewInFabric: true,
        availability: "search-and-assist"
      },
      multipleInstallationsAllowed: true
    }
  },
  GMAIL: {
    Icon: IconBigIcon48Gmail,
    IconSidebar: React__default.Fragment,
    IconPath: React__default.Fragment,
    IconCardPreview: React__default.Fragment,
    IconCardStore: IconBigIcon48Gmail,
    integrationName: "Gmail",
    successMessage: "Your Gmail is now connected.",
    successDescription: "Fabric will now start syncing your Gmail.",
    getTooltip: (root) => `Gmail - ${root.name}`,
    getToPath: (root) => generatePath(appRoutes.integrations.detail.view, {
      integrationId: root.id
    }),
    isSingleMirror: false,
    isDeviceSync: false,
    category: "other",
    installation: {
      minimumTier: SubscriptionTier.PRO,
      description: "Connect your Gmail.",
      infoList: {
        syncDirection: "one-way",
        viewInFabric: true,
        availability: "search-and-assist"
      },
      multipleInstallationsAllowed: true,
      availableSoon: true
    }
  }
};
const getIntegrationConfig = (integration) => {
  const configKey = connectionsSupportedByFe.find((supportedIntegration) => supportedIntegration.toLowerCase() === integration?.toLowerCase());
  if (configKey) {
    const config = connectionsConfig[configKey];
    return {
      ...config,
      IconProps: {
        ...config.IconProps,
        style: {
          color: cssVar["color-text-primary"],
          ...config.IconProps?.style
        }
      }
    };
  }
  return null;
};
const futureConnections = [
  connectionInfoIconFactory({
    name: "Figma",
    description: "Connect your Figma to see all your Figma files inside Fabric.",
    icon: IconFigma,
    iconProps: {
      style: {
        width: 38,
        height: 38
      }
    }
  }),
  connectionInfoIconFactory({
    name: "Readwise",
    description: "Connect your Readwise to sync all your PDFs, EPUBs, bookmarks and more into Fabric.",
    icon: IconReadwise,
    iconProps: {
      style: {
        width: 36
      }
    }
  }),
  connectionInfoIconFactory({
    name: "Bookmarks",
    description: "Connect your Chrome bookmarks and see them inside Fabric.",
    icon: IconChrome,
    iconProps: {
      style: {
        width: 36
      }
    }
  }),
  connectionInfoIconFactory({
    name: "Linear",
    description: "See all your Linear issues, live in Fabric.",
    icon: IconLinear,
    iconProps: {
      style: {
        width: 52
      }
    }
  }),
  connectionInfoIconFactory({
    name: "Dropbox",
    description: "Connect your Dropbox to Fabric. Any changes you make to files or folders will be live updated in Fabric.",
    icon: IconDropbox,
    iconProps: {
      style: {
        width: 32
      }
    }
  }),
  connectionInfoImageFactory({
    name: "Email",
    description: "Connect your email account, and explore all your emails inside Fabric.",
    image: "/images/email-connection.png",
    imageProps: {
      width: 36,
      height: 36
    }
  }),
  connectionInfoIconFactory({
    name: "History",
    description: "Connect your Chrome browser history and get perfect memory over every website you visit.",
    icon: IconChrome,
    iconProps: {
      style: {
        width: 36
      }
    }
  }),
  connectionInfoIconFactory({
    name: "OneDrive",
    description: "Connect your OneDrive to Fabric. Any changes you make to files or folders will be live updated in Fabric.",
    icon: IconOnedrive,
    iconProps: {
      style: {
        width: 36
      }
    }
  }),
  connectionInfoIconFactory({
    name: "iCloud",
    description: "Connect your iCloud to Fabric. Any changes you make to files or folders will be live updated in Fabric.",
    icon: IconApple,
    iconProps: {
      style: {
        width: 36
      }
    }
  })
];
const lateFutureConnections = [
  connectionInfoIconFactory({
    name: "Zapier",
    description: [
      "Integrate the web applications you use and automate workflows.",
      "Connect to more than 5,000 apps."
    ],
    icon: IconZapier,
    iconProps: {
      style: {
        width: 36
      }
    }
  }),
  connectionInfoImageFactory({
    name: "Build your own",
    description: [
      "Use the Fabric API to build your own integration, connecting your favourite app or even an internal company tool.",
      "Share what you build with us and we’ll feature it here."
    ],
    image: Img3dHammer,
    imageProps: {
      width: 40,
      height: 40
    }
  })
];
const getIntegrationViewConfig = (integration) => {
  const config = getIntegrationConfig(integration.type);
  if (config) {
    const getArg = {
      id: integration.id,
      name: integration.displayName
    };
    const isSingleMirror = integration.mirrorRegistries.length === 1;
    return {
      ...config,
      displayName: isSingleMirror ? integration.mirrorRegistries[0].name : integration.displayName,
      tooltipText: config.getTooltip(getArg),
      href: config.getToPath(integration),
      successMessage: config.successMessage,
      successDescription: config.successDescription,
      navigateTo: isSingleMirror ? `/folders/${integration.mirrorRegistries[0].rootId}` : ""
    };
  } else {
    return {
      Icon: React__default.Fragment,
      IconSidebar: React__default.Fragment,
      IconPath: React__default.Fragment,
      IconCardPreview: React__default.Fragment,
      IconCardStore: React__default.Fragment,
      IconProps: {},
      navigateTo: "#",
      displayName: "Unknown",
      integrationName: "Unknown Integration",
      tooltipText: "Unknown Integration",
      successMessage: "Unknown Integration",
      successDescription: "Unknown Integration",
      href: "#",
      isSingleMirror: false,
      isDeviceSync: false
    };
  }
};

export { connectionsConfig, futureConnections, getIntegrationConfig, getIntegrationViewConfig, lateFutureConnections };
//# sourceMappingURL=connections.config.js.map
